import type { ConversationState } from "@ai-admin/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { useSession } from "../../app/session.js";
import { queryClient, trpc, trpcErrorCode } from "../../app/trpc.js";
import { Badge, Button, EXTRA_WIDE_QUERY, focusRing, Notice, POINTER_QUERY, Spinner, Textarea, useMediaQuery } from "../../ui/index.js";
import { fmtDateTime, fmtTime, useCabinet } from "../cabinet/index.js";
import { BookingSheet, statusTone } from "../calendar/index.js";
import { ClientPanel } from "./ClientPanel.js";
import { stateTone } from "./state.js";

/**
 * Диалог: переписка (что ответил бот, что мастер), «взять диалог» (кто первым нажал, тот и ведёт),
 * «вернуть боту», пауза, ответ из кабинета (окно 7 дней), записи из этого диалога, пометка «неверный ответ бота».
 * На телефоне занимает экран целиком; на широком экране живёт в правой колонке `ConversationsPage`,
 * от `xl` рядом с перепиской карточка клиента. С клавиатуры Enter отправляет, Shift+Enter переносит строку.
 */
export function ChatPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { conversationId = "" } = useParams();
  const cab = useCabinet();
  const session = useSession();
  const [text, setText] = useState("");
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [marked, setMarked] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);
  const keyboard = useMediaQuery(POINTER_QUERY);
  const extraWide = useMediaQuery(EXTRA_WIDE_QUERY);

  const messages = useQuery(trpc.inbox.messages.queryOptions({ conversationId, limit: 100 }, { refetchInterval: 5_000 }));
  const details = useQuery(trpc.cabinet.conversation.queryOptions({ conversationId }, { refetchInterval: 15_000 }));
  const refresh = () => {
    void queryClient.invalidateQueries(trpc.inbox.pathFilter());
    void queryClient.invalidateQueries(trpc.cabinet.conversation.pathFilter());
  };
  const take = useMutation(trpc.inbox.takeOver.mutationOptions({ onSettled: refresh }));
  const back = useMutation(trpc.inbox.returnToBot.mutationOptions({ onSuccess: refresh }));
  const pause = useMutation(trpc.inbox.pause.mutationOptions({ onSuccess: refresh }));
  const send = useMutation(
    trpc.inbox.send.mutationOptions({
      onSuccess: refresh,
      // Не отправилось: возвращаем текст в поле, если мастер не начал новый.
      onError: (_e, v) => setText((cur) => cur || v.text),
    }),
  );
  const feedback = useMutation(trpc.bot.feedback.set.mutationOptions({ onSuccess: (_d, v) => setMarked((s) => new Set(s).add(v.messageId)) }));

  const items = messages.data?.messages ?? [];
  const lastId = items.at(-1)?.id;
  useEffect(() => {
    if (lastId && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [lastId]);

  if (messages.isPending || details.isPending) return <Spinner />;
  if (messages.isError || details.isError) return <Notice kind="error">{t("common.error")}</Notice>;

  const d = details.data;
  const conv = d.conversation;
  const state = conv.state as ConversationState;
  const me = session.user?.id;
  const ledByOther = state === "staff" && conv.assignedUserId && conv.assignedUserId !== me;
  const blockedByLead = ledByOther && !cab.isOwner;
  const canReply = d.replyWindowOpen && !blockedByLead;
  const busy = take.isPending || back.isPending || pause.isPending;

  const submit = () => {
    const value = text.trim();
    if (!value || !canReply || send.isPending) return;
    setText("");
    send.mutate({ conversationId, text: value });
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit();
  };
  // Только с физической клавиатурой: на телефоне Enter по-прежнему переносит строку.
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!keyboard || e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  };
  const clientTitle = d.client?.name || (d.client?.instagram ? `@${d.client.instagram}` : t("conv.instagramClient"));

  return (
    // Высота на телефоне: экран минус шапка кабинета (вместе со строкой «‹ Назад», около 1,4rem), нижняя панель и отступ под ней.
    // На широком экране высоту задаёт колонка списка диалогов.
    <div className="flex h-[calc(100dvh-12.875rem-env(safe-area-inset-bottom))] gap-6 lg:h-full">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold">{clientTitle}</div>
              {d.client && !extraWide && (
                <Link to={`${cab.base}/clients/${d.client.id}`} className="text-sm text-slate-500 underline hover:text-slate-900">
                  {t("conv.clientCard")}
                </Link>
              )}
            </div>
            <Badge tone={stateTone[state]}>{state === "staff" && d.assignedName ? t("conv.ledBy", { name: d.assignedName }) : t(`conv.state.${state}`)}</Badge>
          </div>
        </div>

        <div className="mb-2 flex flex-wrap gap-2">
          {(state === "escalated" || state === "bot" || state === "paused" || (state === "staff" && !conv.assignedUserId)) && (
            <Button className="px-3 py-2 text-sm" disabled={busy} onClick={() => take.mutate({ conversationId })}>
              {t("conv.takeOver")}
            </Button>
          )}
          {state !== "bot" && !blockedByLead && (
            <Button variant="secondary" className="px-3 py-2 text-sm" disabled={busy} onClick={() => back.mutate({ conversationId })}>
              {t("conv.returnToBot")}
            </Button>
          )}
          {state !== "paused" && !blockedByLead && (
            <Button variant="ghost" className="px-3 py-2 text-sm" disabled={busy} onClick={() => pause.mutate({ conversationId })}>
              {t("conv.pause")}
            </Button>
          )}
        </div>
        {take.isError && (
          <Notice kind="error">
            {trpcErrorCode(take.error) !== "conflict"
              ? t("common.error")
              : d.assignedName
                ? t("conv.alreadyTaken", { name: d.assignedName })
                : t("conv.alreadyTakenSomeone")}
          </Notice>
        )}
        {blockedByLead && <Notice>{d.assignedName ? t("conv.ledByOther", { name: d.assignedName }) : t("conv.ledByOtherSomeone")}</Notice>}
        {state === "paused" && <Notice>{t("conv.pausedHint")}</Notice>}

        {d.bookings.length > 0 && (
          <div className="-mx-4 my-2 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:px-0">
            {d.bookings.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBookingId(b.id)}
                className={`shrink-0 rounded-xl border border-slate-200 px-3 py-2 text-left text-sm hover:border-slate-400 ${focusRing}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{fmtDateTime(b.period.start, cab.timezone, lang)}</span>
                  <Badge tone={statusTone[b.status]}>{t(`cal.status.${b.status}`)}</Badge>
                </div>
                <div className="text-slate-500">
                  {b.items.map((i) => i.name).join(" + ")}
                  {b.fromThisConversation && ` · ${t("conv.fromThisChat")}`}
                </div>
              </button>
            ))}
          </div>
        )}

        <div ref={listRef} role="log" aria-label={t("conv.messages")} className="flex-1 space-y-2 overflow-y-auto rounded-2xl bg-slate-50 p-3">
          {items.length === 0 && <p className="text-center text-sm text-slate-500">{t("conv.noMessages")}</p>}
          {items.map((m) => {
            if (m.author === "system") {
              return (
                <div key={m.id} className="text-center text-xs text-slate-500">
                  {m.text}
                </div>
              );
            }
            const mine = m.author !== "client";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.author === "client" ? "bg-white" : m.author === "bot" ? "bg-blue-100" : "bg-slate-900 text-white"}`}
                >
                  <div className="mb-0.5 flex items-center gap-2 text-xs opacity-70">
                    <span>{t(`conv.author.${m.author}`)}</span>
                    <span>{fmtTime(m.createdAt, cab.timezone, lang)}</span>
                    {m.sensitive && <Badge tone="red">{t("conv.sensitive")}</Badge>}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.text ?? (m.kind === "voice" ? t("conv.voice") : "…")}</div>
                  {m.deliveryStatus === "failed" && <div className="mt-1 text-xs text-red-600">{t("conv.notDelivered")}</div>}
                  {m.deliveryStatus === "pending" && <div className="mt-1 text-xs opacity-60">{t("conv.sending")}</div>}
                  {m.author === "bot" &&
                    (marked.has(m.id) ? (
                      <div className="mt-1 text-xs text-slate-500">{t("conv.markedWrong")}</div>
                    ) : (
                      <button
                        type="button"
                        className="mt-1 text-xs text-slate-500 underline hover:text-slate-900"
                        disabled={feedback.isPending}
                        onClick={() => feedback.mutate({ messageId: m.id, verdict: "wrong" })}
                      >
                        {t("conv.markWrong")}
                      </button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>

        {!d.replyWindowOpen && <p className="mt-2 text-sm text-slate-500">{t("conv.windowClosed")}</p>}
        {send.isError && <Notice kind="error">{t("common.error")}</Notice>}
        <form onSubmit={onSubmit} className="mt-2 flex items-end gap-2">
          <Textarea
            rows={1}
            value={text}
            disabled={!canReply}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={canReply ? t("conv.replyPlaceholder") : ""}
            className="max-h-32 min-h-12 resize-none py-2"
          />
          <Button type="submit" disabled={!canReply || !text.trim() || send.isPending}>
            {t("conv.send")}
          </Button>
        </form>
        {canReply && (state !== "staff" || keyboard) && (
          <p className="mt-1 text-xs text-slate-500">
            {state !== "staff" && t("conv.replyHint")}
            {state !== "staff" && keyboard && " "}
            {keyboard && t("conv.enterHint")}
          </p>
        )}
      </div>

      {extraWide && d.client && <ClientPanel clientId={d.client.id} />}

      <BookingSheet bookingId={bookingId} onClose={() => setBookingId(null)} />
    </div>
  );
}
